/**
 * Canvas notes (#157) — the geometry and ordering rules, with no rendering in
 * them, so the canvas, the parts list and the printed notes page all agree
 * about where a callout lands and what number a note carries.
 *
 * See `Note` / `NoteAnchor` in `Project.ts` for why an anchor is stored as a
 * position along the part's outline rather than as an absolute point.
 */
import paper from "paper";
import { pieceStitchLines } from "./Project";
import type { Note, NoteAnchor, Piece, Project, Vec2 } from "./Project";
import { seamPathData } from "./seamGeometry";

/** Smallest note box a drag can produce, in project mm. Below this the box has
 *  no room for even one wrapped word and the resize handles overlap. */
export const MIN_NOTE_SIZE_MM = 8;

/** Defaults for a freshly placed note — a box big enough for a short sentence
 *  at a size that stays readable when the pattern is printed at 1:1. */
export const DEFAULT_NOTE_WIDTH_MM = 60;
export const DEFAULT_NOTE_HEIGHT_MM = 20;
export const DEFAULT_NOTE_FONT_MM = 4;

/**
 * The outline to measure an anchor against.
 *
 * `shapePathData` can be a compound path (a part with an island, or one that
 * booleans left in several regions). The callout should ride the part's actual
 * silhouette, so the longest subpath wins rather than whichever happens to be
 * first.
 */
function outlineFor(
  pathData: string,
  scope: paper.PaperScope,
): paper.Path | null {
  if (!pathData) return null;
  const item = new scope.CompoundPath({ pathData, insert: false });
  const paths: paper.Path[] =
    item.children && item.children.length > 0
      ? (item.children.filter((c) => c instanceof scope.Path) as paper.Path[])
      : item instanceof scope.Path
        ? [item as unknown as paper.Path]
        : [];
  if (paths.length === 0) return null;
  let best = paths[0]!;
  for (const p of paths) if (p.length > best.length) best = p;
  return best;
}

/**
 * What a callout points at: a part, or one of the things drawn on it (#1076).
 *
 * The finer ids are optional refinements of `pieceId`, not alternatives to it —
 * see `NoteAnchor.pieceId`. At most one of them is ever set.
 */
export interface NoteTarget {
  pieceId: string;
  cutoutId?: string;
  stitchId?: string;
}

/**
 * The geometry a target's callout rides, as SVG path data.
 *
 * A cutout carries its outline directly; a seam is a polyline plus bezier
 * handles, so it goes through `seamPathData` — the same source the renderer,
 * the exporters and the knife read, so a note can't anchor to a shape of the
 * seam nobody else draws. Falls back to the part's own silhouette, which is
 * both the un-refined case and what an anchor whose cutout / seam has since
 * been deleted lands on.
 */
export function noteTargetPathData(piece: Piece, target: NoteTarget): string {
  if (target.cutoutId) {
    const cutout = piece.cutouts.find((c) => c.id === target.cutoutId);
    if (cutout) return cutout.pathData;
  }
  if (target.stitchId) {
    const line = pieceStitchLines(piece).find((l) => l.id === target.stitchId);
    if (line) return seamPathData(line);
  }
  return piece.shapePathData;
}

/** The target an anchor addresses, dropping the fields it doesn't carry so the
 *  key below stays stable. */
export function noteAnchorTarget(anchor: NoteAnchor): NoteTarget {
  const target: NoteTarget = { pieceId: anchor.pieceId };
  if (anchor.cutoutId) target.cutoutId = anchor.cutoutId;
  if (anchor.stitchId) target.stitchId = anchor.stitchId;
  return target;
}

/**
 * One string per THING a note can point at.
 *
 * Used wherever two anchors have to be told apart — deduping a re-aimed
 * callout, keying the chips at both ends of the link. Comparing `pieceId`
 * alone was right while a part was the only target; it would now collapse a
 * note on a seam into a note on the part the seam sits on.
 */
export function noteTargetKey(target: NoteTarget): string {
  return `${target.pieceId}|${target.cutoutId ?? ""}|${target.stitchId ?? ""}`;
}

/**
 * How a target reads on its own part — "Cutout 1", "Stitch 2" — or null when
 * the target IS the part, which needs no naming where the part is already the
 * heading (#1076).
 *
 * The index fallbacks match the rows in the parts list exactly, so the printed
 * page, the panel and the drawing all call the same object by the same name.
 * Returns null for a cutout or seam that has since been deleted: there is
 * nothing left to name, and the note still belongs under its part.
 */
export function noteTargetLabel(
  piece: Piece,
  target: NoteTarget,
): string | null {
  if (target.cutoutId) {
    const idx = piece.cutouts.findIndex((c) => c.id === target.cutoutId);
    if (idx < 0) return null;
    return piece.cutouts[idx]!.name ?? `Cutout ${idx + 1}`;
  }
  if (target.stitchId) {
    const lines = pieceStitchLines(piece);
    const idx = lines.findIndex((l) => l.id === target.stitchId);
    if (idx < 0) return null;
    return lines[idx]!.name ?? `Stitch ${idx + 1}`;
  }
  return null;
}

/**
 * Build an anchor from the point the maker pressed on a part.
 *
 * The pressed point is preserved exactly — `t` finds the nearest spot on the
 * outline and `offsetMm` records how far off it the press was, so pressing in
 * the middle of a part anchors the callout in the middle of the part rather
 * than snapping it out to the edge.
 *
 * `target` names which outline is ridden. Pressing on a seam anchors to the
 * SEAM, so the tip travels with it when the seam alone is moved — pinning to
 * the part instead would leave the callout pointing at bare leather.
 */
export function noteAnchorFor(
  piece: Piece,
  point: Vec2,
  scope: paper.PaperScope,
  target: NoteTarget = { pieceId: piece.id },
): NoteAnchor | null {
  const path = outlineFor(noteTargetPathData(piece, target), scope);
  if (!path || path.length === 0) return null;
  const press = new scope.Point(point.x, point.y);
  const loc = path.getNearestLocation(press);
  if (!loc) return null;
  const t = loc.offset / path.length;
  // Signed along the outline's normal, so the sign survives the round trip and
  // an interior anchor stays interior.
  const n = loc.normal.normalize();
  const offsetMm = press.subtract(loc.point).dot(n);
  const anchor: NoteAnchor = { pieceId: piece.id, t, offsetMm };
  if (target.cutoutId) anchor.cutoutId = target.cutoutId;
  if (target.stitchId) anchor.stitchId = target.stitchId;
  return anchor;
}

/** Where a note's callout tip currently sits, or null when the note is free or
 *  its part has since been deleted.
 *
 *  A cutout or seam that has since been deleted falls back to the part's own
 *  outline rather than dropping the callout: the note still has something true
 *  to point at, and losing the line entirely would look like the note came
 *  unstuck on its own. */
export function resolveNoteAnchor(
  anchor: NoteAnchor,
  project: Project,
  scope: paper.PaperScope,
): Vec2 | null {
  const piece = project.pieces.find((p) => p.id === anchor.pieceId);
  if (!piece) return null;
  const path = outlineFor(
    noteTargetPathData(piece, noteAnchorTarget(anchor)),
    scope,
  );
  if (!path || path.length === 0) return null;
  const clamped = Math.min(1, Math.max(0, anchor.t));
  const loc = path.getLocationAt(clamped * path.length);
  if (!loc) return null;
  const n = loc.normal.normalize().multiply(anchor.offsetMm);
  const pt = loc.point.add(n);
  return { x: pt.x, y: pt.y };
}

/**
 * A note's callouts, as a list — the one place that knows the field can also
 * arrive in the single-anchor form earlier builds wrote.
 *
 * Everything else reads through this, so no other module has to remember the
 * old shape, and nothing writes it back: a note edited once comes out as a
 * list.
 */
export function noteAnchors(note: Note): NoteAnchor[] {
  if (note.anchors) return note.anchors;
  return note.anchor ? [note.anchor] : [];
}

/** Every resolved callout tip for a note, skipping any whose part is gone. */
export function resolveNoteAnchors(
  note: Note,
  project: Project,
  scope: paper.PaperScope,
): { anchor: NoteAnchor; point: Vec2 }[] {
  const out: { anchor: NoteAnchor; point: Vec2 }[] = [];
  for (const anchor of noteAnchors(note)) {
    const point = resolveNoteAnchor(anchor, project, scope);
    if (point) out.push({ anchor, point });
  }
  return out;
}

/** The note with `anchors` written and the legacy field dropped — the one way
 *  to rewrite a note's callouts, so the deprecated shape can never come back. */
export function withNoteAnchors(note: Note, anchors: NoteAnchor[]): Note {
  const next: Note = { ...note, anchors };
  delete next.anchor;
  if (anchors.length === 0) delete next.anchors;
  return next;
}

/** Every note, in list order, paired with the number it shows. Numbering is
 *  GLOBAL and follows list order, so a note keeps one identity whether it is
 *  read on the canvas, in the parts list or on the printed page — the page
 *  groups them by part but does not renumber within a group. */
export interface NumberedNote {
  note: Note;
  number: number;
}

export function numberedNotes(project: Project): NumberedNote[] {
  return (project.notes ?? []).map((note, i) => ({ note, number: i + 1 }));
}

/** A note is drawn when neither it nor the global toggle has hidden it. The
 *  global toggle is a viewing preference and lives outside the project; this
 *  covers only the per-note flag. */
export function isNoteVisible(note: Note): boolean {
  return note.visible !== false;
}

/**
 * The printed notes page, grouped (#157).
 *
 * Parts come first in the order they appear in the project, each with the notes
 * pointing at it, then everything unattached under one general heading. A part
 * with no notes is left out — the page lists notes, not parts.
 */
/**
 * A note as it appears under one part's heading (#1076).
 *
 * `on` is what the note points at ON that part when it is not the part itself,
 * so the printed page can say "Cutout 1" rather than leaving the maker to work
 * out which of five seams a note about stitching means.
 */
export interface GroupedNote extends NumberedNote {
  /** Named targets on this part, joined — absent when the note is about the
   *  part's own outline. */
  on?: string;
}

export interface NoteGroup {
  /** Absent for the catch-all group of notes that point at nothing. */
  piece?: Piece;
  title: string;
  notes: GroupedNote[];
}

/** Heading for notes that aren't attached to any part. */
export const UNATTACHED_NOTES_TITLE = "General";

/**
 * One note under one part's heading, carrying what on that part it names.
 *
 * A note that ALSO points at the part's own outline gets no qualifier: it is
 * about the whole piece, and naming one seam it happens to touch as well would
 * understate it. Duplicates are collapsed, since two lines to the same object
 * say one thing.
 */
function groupedFor(entry: NumberedNote, piece: Piece): GroupedNote {
  const mine = noteAnchors(entry.note).filter((a) => a.pieceId === piece.id);
  if (mine.some((a) => !a.cutoutId && !a.stitchId)) return entry;
  const labels: string[] = [];
  for (const a of mine) {
    const label = noteTargetLabel(piece, noteAnchorTarget(a));
    if (label && !labels.includes(label)) labels.push(label);
  }
  return labels.length > 0 ? { ...entry, on: labels.join(", ") } : entry;
}

export function groupNotesByPart(project: Project): NoteGroup[] {
  const numbered = numberedNotes(project);
  const groups: NoteGroup[] = [];

  for (const piece of project.pieces) {
    const mine = numbered
      .filter((n) => noteAnchors(n.note).some((a) => a.pieceId === piece.id))
      .map((n) => groupedFor(n, piece));
    if (mine.length === 0) continue;
    groups.push({
      piece,
      title: piece.name || "Untitled part",
      notes: mine,
    });
  }

  // Anything whose anchor is missing OR points at a part that no longer exists
  // — a deleted part must not take its notes' text off the page with it.
  const pieceIds = new Set(project.pieces.map((p) => p.id));
  // Unattached, or pointing only at parts that have since been deleted. A note
  // pointing at two live parts is listed under BOTH — it is about both — and so
  // is not loose.
  const loose = numbered.filter(
    (n) => !noteAnchors(n.note).some((a) => pieceIds.has(a.pieceId)),
  );
  if (loose.length > 0) {
    groups.push({ title: UNATTACHED_NOTES_TITLE, notes: loose });
  }

  return groups;
}

/**
 * Wrap `text` to `widthMm` at `fontSizeMm`, returning the lines to draw.
 *
 * Paper.js `PointText` has no multi-line layout (the same gap `DimensionLabel`
 * works around), and the PDF page needs identical wrapping so a note reads the
 * same on screen and on paper. `measure` is supplied by the caller because only
 * it knows the real font metrics — a canvas 2D context on screen, jsPDF's
 * string width on the page.
 *
 * Explicit newlines in the note are honored as hard breaks. A single word wider
 * than the box overflows on its own line rather than being chopped, which reads
 * better than a mid-word break for the part codes and measurements that end up
 * in notes.
 */
export function wrapNoteText(
  text: string,
  widthMm: number,
  measure: (line: string) => number,
): string[] {
  const out: string[] = [];
  for (const hard of text.split("\n")) {
    const words = hard.split(/\s+/).filter((w) => w.length > 0);
    if (words.length === 0) {
      out.push("");
      continue;
    }
    let line = "";
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (line && measure(candidate) > widthMm) {
        out.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) out.push(line);
  }
  return out;
}

/** Text a new note starts with. Not empty: a blank card is something the maker
 *  has to guess is editable. The panel selects it on focus so the first
 *  keystroke replaces it. */
/**
 * What the empty text box says before anything is typed into it.
 *
 * A new note starts EMPTY rather than pre-filled: placeholder words that live
 * in the model get printed, exported and listed like anything the maker wrote,
 * and "New note" on a finished pattern reads as a mistake. A placeholder is
 * the same invitation without the risk — it is never text, so it can never be
 * mistaken for text.
 */
export const NOTE_PLACEHOLDER = "Type your note here…";

/**
 * A note's text as one short line, for a list row (#157).
 *
 * Notes are written to be read on the drawing, so their text runs to several
 * lines and the row would otherwise stretch the panel. The first line, clipped,
 * is enough to tell one note from another — the whole text is a click away in
 * the note's own panel.
 */
export function noteRowLabel(text: string): string {
  const first =
    text
      .split("\n")
      .find((l) => l.trim().length > 0)
      ?.trim() ?? "";
  if (first.length === 0) return "(empty note)";
  return first.length > NOTE_ROW_LABEL_MAX
    ? `${first.slice(0, NOTE_ROW_LABEL_MAX - 1).trimEnd()}…`
    : first;
}

const NOTE_ROW_LABEL_MAX = 40;

/**
 * Reorder notes in the list (#157), moving `movedIds` to sit immediately before
 * `beforeId` (or to the end when it is null).
 *
 * Numbering is list position — `numberedNotes` counts down the array — so this
 * is also what renumbers the badges on the drawing and the lines on the printed
 * page. That is the point: a maker orders notes the way the work is done, and
 * expects "1" to be the first thing to do.
 *
 * Returns the input project when nothing moves, so a drop that changes nothing
 * costs no undo entry.
 */
export function moveNotesInList(
  project: Project,
  movedIds: readonly string[],
  beforeId: string | null,
): Project {
  const moved = new Set(movedIds);
  if (moved.size === 0) return project;
  const list = project.notes ?? [];
  const movedInList = list.filter((n) => moved.has(n.id));
  if (movedInList.length === 0) return project;
  const kept = list.filter((n) => !moved.has(n.id));
  let idx =
    beforeId == null ? kept.length : kept.findIndex((n) => n.id === beforeId);
  if (idx < 0) idx = kept.length;
  const notes = [...kept.slice(0, idx), ...movedInList, ...kept.slice(idx)];
  const unchanged =
    notes.length === list.length && notes.every((n, i) => n === list[i]);
  if (unchanged) return project;
  return { ...project, notes };
}
